<template>
  <div class="invoice-card">
    <div class="invoice-header">
      <div class="invoice-number">#{{ invoiceData.invoice_no }}</div>
      <!-- Display invoice status as badge (read-only) -->
      <div class="invoice-status-wrapper">
        <b-badge :variant="getInvoiceStatusVariant(invoiceData.status)">
          {{ invoiceData.status || "-" }}
        </b-badge>
      </div>
      <!-- COMMENTED OUT: Change Invoice Status Feature -->
      <!-- <div class="invoice-status-wrapper">
        <ChangeInvoiceStatusDropDown
          name="invoiceCard"
          endpoint="invoices"
          :data="invoiceData"
          @onEmit="$emit('onChangeStatus')"
        />
      </div> -->
    </div>
    <div class="invoice-amount">
      {{ formatPrice(invoiceData.amount) }}
    </div>
    <div v-if="invoiceData.description" class="invoice-description">
      {{ invoiceData.description }}
    </div>
    <div class="invoice-footer">
      <div class="invoice-date">
        <feather-icon icon="CalendarIcon" size="14" />
        <span>{{ invoiceData.date || "—" }}</span>
      </div>
      <div class="invoice-type">
        <feather-icon icon="TagIcon" size="14" />
        <span>{{ invoiceTypeName }}</span>
      </div>
    </div>
    <!-- COMMENTED OUT: Print Invoice Feature -->
    <!-- <div v-if="showActions" class="invoice-actions">
      <slot name="actions" :invoice="invoiceData">
        <b-button
          v-if="
            invoiceData.status !== 'printed' &&
            invoiceData.status === 'approved'
          "
          variant="primary"
          size="sm"
          @click="showPrintModal"
        >
          <feather-icon icon="PrinterIcon" size="14" />
          {{ $t("reception.Print Invoice") }}
        </b-button>
      </slot>
    </div> -->

    <!-- Print Modals -->
    <b-modal
      :id="`modal-confirm-print-${invoiceData.id}`"
      :no-close-on-backdrop="true"
      :title="$t('surgery.print-invoice')"
      ok-only
      :ok-title="$t('common.print')"
      @ok="handlePrintOk"
      @hide="handlePrintHide"
    >
      <b-card-text>
        <h5>{{ $t("surgery.print-invoice") }}</h5>
        {{ $t("surgery.print-sure") }}
      </b-card-text>
    </b-modal>

    <!-- Print Components -->
    <!-- NICU Invoice -->
    <NICUInvoice
      v-if="invoiceData.type === 'invoice_nicu' && currentInvoiceForPrint"
      :patient-data="patientDataForPrint"
      :data="currentInvoiceForPrint"
      :incubator-name="incubatorNameForPrint"
    />

    <!-- Out Clinic Invoice -->
    <OutClinicInvoice
      v-if="invoiceData.type === 'invoice_out_clinic' && currentInvoiceForPrint"
      :patient-data="patientDataForPrint"
      :data="currentInvoiceForPrint"
    />

    <!-- Emergency Invoice -->
    <EmergencyInvoice
      v-if="invoiceData.type === 'invoice_emergency' && currentInvoiceForPrint"
      :patient-data="patientDataForPrint"
      :data="currentInvoiceForPrint"
    />

    <!-- Test Invoice -->
    <TestInvoice
      v-if="
        invoiceData.type === 'invoice_patient_card_test' &&
        currentInvoiceForPrint
      "
      :patient-data="getPatientDataForTestInvoice()"
      :data="currentInvoiceForPrint"
    />

    <!-- Refund Invoice -->
    <RefundInvoice
      v-if="invoiceData.type === 'refund' && currentInvoiceForPrint"
      :patient-data="patientDataForPrint"
      :data="currentInvoiceForPrint"
    />

    <!-- Surgery Insurance Invoice -->
    <SurgeryInsuranceInvoice
      v-if="
        invoiceData.type === 'invoice_insurance_surgery' &&
        currentInvoiceForPrint
      "
      :patient-data="patientDataForPrint"
      :data="currentInvoiceForPrint"
    />

    <!-- Hidden Print Buttons -->
    <b-button
      v-if="invoiceData.type === 'invoice_nicu'"
      :ref="`NICUInvoice-${invoiceData.id}`"
      v-print="NICUInvoiceConfig"
      variant="outline-primary"
      size="sm"
      class="hidden"
    >
      {{ $t("reception.Print Invoice") }}
    </b-button>

    <b-button
      v-if="invoiceData.type === 'invoice_out_clinic'"
      :ref="`OutClinicInvoice-${invoiceData.id}`"
      v-print="OutClinicInvoiceConfig"
      variant="outline-primary"
      size="sm"
      class="hidden"
    >
      {{ $t("reception.Print Invoice") }}
    </b-button>

    <b-button
      v-if="invoiceData.type === 'invoice_emergency'"
      :ref="`EmergencyInvoice-${invoiceData.id}`"
      v-print="EmergencyInvoiceConfig"
      variant="outline-primary"
      size="sm"
      class="hidden"
    >
      {{ $t("reception.Print Invoice") }}
    </b-button>

    <b-button
      v-if="invoiceData.type === 'invoice_patient_card_test'"
      :ref="`TestInvoice-${invoiceData.id}`"
      v-print="TestInvoiceConfig"
      variant="outline-primary"
      size="sm"
      class="hidden"
    >
      {{ $t("reception.Print Invoice") }}
    </b-button>

    <b-button
      v-if="invoiceData.type === 'refund'"
      :ref="`RefundInvoice-${invoiceData.id}`"
      v-print="RefundInvoiceConfig"
      variant="outline-primary"
      size="sm"
      class="hidden"
    >
      {{ $t("reception.Print Invoice") }}
    </b-button>

    <b-button
      v-if="invoiceData.type === 'invoice_insurance_surgery'"
      :ref="`SurgeryInsuranceInvoice-${invoiceData.id}`"
      v-print="SurgeryInsuranceInvoiceConfig"
      variant="outline-primary"
      size="sm"
      class="hidden"
    >
      {{ $t("reception.Print Invoice") }}
    </b-button>
  </div>
</template>

<script>
import { BBadge, BButton, BModal, BCardText } from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { getNestedProperty } from "@/utils/nestedProperty";

// Lazy load print components
const NICUInvoice = () => import("@/components/printables/NICUInvoice.vue");
const OutClinicInvoice = () =>
  import("@/components/printables/OutClinicInvoice.vue");
const EmergencyInvoice = () =>
  import("@/components/printables/EmergencyInvoice.vue");
const TestInvoice = () => import("@/components/printables/TestInvoice.vue");
const RefundInvoice = () => import("@/components/printables/RefundInvoice.vue");
const SurgeryInsuranceInvoice = () =>
  import("@/components/printables/SurgeryInsuranceInvoice.vue");
const ChangeInvoiceStatusDropDown = () =>
  import("@/components/invoices/ChangeInvoiceStatusDropDown.vue");

export default {
  name: "InvoiceCard",
  components: {
    BBadge,
    BButton,
    BModal,
    BCardText,
    NICUInvoice,
    OutClinicInvoice,
    EmergencyInvoice,
    TestInvoice,
    RefundInvoice,
    SurgeryInsuranceInvoice,
    ChangeInvoiceStatusDropDown,
  },
  props: {
    invoice: {
      type: Object,
      required: true,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentInvoiceForPrint: null,
      patientDataForPrint: null,
      incubatorNameForPrint: null,
      loading: false,
      NICUInvoiceConfig: {
        id: "NICUInvoice",
      },
      OutClinicInvoiceConfig: {
        id: "OutClinicInvoice",
      },
      EmergencyInvoiceConfig: {
        id: "EmergencyInvoice",
      },
      TestInvoiceConfig: {
        id: "TestInvoice",
      },
      RefundInvoiceConfig: {
        id: "RefundInvoice",
      },
      SurgeryInsuranceInvoiceConfig: {
        id: "SurgeryInsuranceInvoice",
      },
    };
  },
  computed: {
    invoiceData() {
      // Filter and return only the data needed for display
      return {
        id: this.invoice.id,
        invoice_no: this.invoice.invoice_no,
        type: this.invoice.type,
        amount: this.invoice.amount,
        discount: this.invoice.discount,
        date: this.invoice.date,
        status: this.invoice.status,
        internal_status: this.invoice.internal_status,
        description: this.invoice.description,
        // Include nested data only if needed
        patient_card: this.invoice.patient_card
          ? {
              id: this.invoice.patient_card.id,
              card_no: this.invoice.patient_card.card_no,
              child_name: this.invoice.patient_card.child_name,
              patient: this.invoice.patient_card.patient
                ? {
                    id: this.invoice.patient_card.patient.id,
                    patient_no: this.invoice.patient_card.patient.patient_no,
                    name: this.invoice.patient_card.patient.name,
                    mother_name: this.invoice.patient_card.patient.mother_name,
                    phone: this.invoice.patient_card.patient.phone,
                    gender: this.invoice.patient_card.patient.gender,
                    birth_date: this.invoice.patient_card.patient.birth_date,
                  }
                : null,
            }
          : null,
        patient_card_incubator: this.invoice.patient_card_incubator
          ? {
              id: this.invoice.patient_card_incubator.id,
              incubator: this.invoice.patient_card_incubator.incubator
                ? {
                    id: this.invoice.patient_card_incubator.incubator.id,
                    name: this.invoice.patient_card_incubator.incubator.name,
                    brand: this.invoice.patient_card_incubator.incubator.brand,
                  }
                : null,
            }
          : null,
        invoice_items: this.invoice.invoice_items || [],
        service: this.invoice.service
          ? {
              id: this.invoice.service.id,
              title: this.invoice.service.title,
              price: this.invoice.service.price,
            }
          : null,
        doctor: this.invoice.doctor
          ? {
              id: this.invoice.doctor.id,
              name: this.invoice.doctor.name,
            }
          : null,
        surgery: this.invoice.surgery
          ? {
              id: this.invoice.surgery.id,
              name: this.invoice.surgery.name,
            }
          : null,
      };
    },
    invoiceTypeName() {
      const typeMap = {
        invoice_out_clinic: "cashiers.invoice-type-out-clinic",
        invoice_nicu: "cashiers.invoice-type-nicu",
        invoice_patient_card_test: "cashiers.invoice-type-patient-card-test",
        refund: "cashiers.invoice-type-refund",
        invoice_insurance_surgery: "cashiers.invoice-type-insurance-surgery",
        invoice_emergency: "cashiers.invoice-type-emergency",
      };

      const translationKey = typeMap[this.invoiceData.type];
      return translationKey ? this.$t(translationKey) : this.invoiceData.type;
    },
  },
  methods: {
    getNestedProperty,
    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : "0 IQD";
    },
    getInvoiceStatusVariant(status) {
      const map = {
        paid: "success",
        pending: "warning",
        cancelled: "danger",
        partial: "info",
        approved: "success",
        printed: "info",
      };
      return map[status] || "secondary";
    },
    showPrintModal() {
      // Prevent double opening
      if (this.loading) return;
      this.$bvModal.show(`modal-confirm-print-${this.invoiceData.id}`);
    },
    handlePrintOk(bvModalEvt) {
      // Prevent default modal close
      bvModalEvt.preventDefault();
      // Call print function
      this.printInvoice();
    },
    handlePrintHide() {
      // Reset loading state when modal is closed
      this.loading = false;
    },
    async printInvoice() {
      try {
        this.loading = true;

        // Build include string based on invoice type
        let includeString = "service,surgery,patientCard,doctor,invoiceItems";

        if (this.invoiceData.type === "invoice_nicu") {
          includeString +=
            ",patientCardIncubator,patientCardIncubator.incubator";
        } else if (this.invoiceData.type === "invoice_patient_card_test") {
          includeString += ",patientCardTest,patientCardTest.test";
        }

        const url = `/invoices?filter[id]=${this.invoiceData.id}&include=${includeString}`;
        await this.getList(url);

        if (
          this.all_data &&
          this.all_data.data &&
          this.all_data.data.length > 0
        ) {
          this.currentInvoiceForPrint = this.all_data.data[0];
          // Pass the full patient_card object with all nested data
          if (this.currentInvoiceForPrint.patient_card) {
            this.patientDataForPrint = {
              ...this.currentInvoiceForPrint.patient_card,
              patient: this.currentInvoiceForPrint.patient_card.patient
                ? { ...this.currentInvoiceForPrint.patient_card.patient }
                : null,
            };
          } else {
            this.patientDataForPrint = null;
          }

          // Get incubator name for NICU invoices
          if (this.invoiceData.type === "invoice_nicu") {
            this.incubatorNameForPrint = getNestedProperty(
              this.currentInvoiceForPrint,
              "patient_card_incubator.incubator.name",
              "N/A"
            );
          }

          await this.$nextTick();

          setTimeout(() => {
            this.triggerPrint();
            this.changeStatusToPrinted();
            // Close modal after print is triggered
            this.$bvModal.hide(`modal-confirm-print-${this.invoiceData.id}`);
          }, 100);
        }
      } catch (error) {
        console.error("Failed to print invoice:", error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.error"),
            variant: "danger",
            text: this.$t("common.error-occurred"),
          },
        });
      } finally {
        this.loading = false;
      }
    },
    triggerPrint() {
      const refName = this.getPrintRefName();
      if (refName && this.$refs[refName]) {
        this.$refs[refName].click();
      }
    },
    getPrintRefName() {
      const typeMap = {
        invoice_nicu: `NICUInvoice-${this.invoiceData.id}`,
        invoice_out_clinic: `OutClinicInvoice-${this.invoiceData.id}`,
        invoice_emergency: `EmergencyInvoice-${this.invoiceData.id}`,
        invoice_patient_card_test: `TestInvoice-${this.invoiceData.id}`,
        refund: `RefundInvoice-${this.invoiceData.id}`,
        invoice_insurance_surgery: `SurgeryInsuranceInvoice-${this.invoiceData.id}`,
      };
      return typeMap[this.invoiceData.type];
    },
    getPatientDataForTestInvoice() {
      // TestInvoice expects patientData to be the patient object directly
      if (
        this.currentInvoiceForPrint &&
        this.currentInvoiceForPrint.patient_card &&
        this.currentInvoiceForPrint.patient_card.patient
      ) {
        return { ...this.currentInvoiceForPrint.patient_card.patient };
      }
      return {};
    },
    changeStatusToPrinted() {
      const params = {
        form: { status: "printed" },
        path: `/invoices/${this.invoiceData.id}/changeStatus`,
      };
      this.$store
        .dispatch("put", params)
        .then((response) => {
          this.$emit("onChangeStatus");
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.success"),
              variant: "success",
              text:
                response.data.message ||
                this.$t("common.invoice-printed-successfully"),
            },
          });
        })
        .catch((error) => {
          if (error.response && error.response.status === 400) {
            for (const item in error.response.data) {
              if (error.response.data.hasOwnProperty(item)) {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "validation error",
                    variant: "danger",
                    text: error.response.data[item][0],
                  },
                });
              }
            }
          }
        });
    },
  },
};
</script>

<style scoped>
.invoice-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  padding: 1.25rem;
  transition: all 0.3s;
}

.invoice-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  border-color: #667eea;
}

.invoice-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.invoice-status-wrapper {
  display: flex;
  align-items: center;
}

.invoice-number {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}

.invoice-status {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
}

.invoice-amount {
  font-size: 1.5rem;
  font-weight: 700;
  color: #22c55e;
  margin-bottom: 0.75rem;
}

.invoice-description {
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 0.75rem;
  line-height: 1.5;
}

.invoice-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.75rem;
  border-top: 1px dashed #cbd5e1;
  gap: 0.5rem;
}

.invoice-date,
.invoice-type {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #64748b;
}

.invoice-actions {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px dashed #cbd5e1;
  display: flex;
  justify-content: flex-end;
}

.invoice-actions button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.hidden {
  display: none !important;
}
</style>
